// P2b tanda 4 — helpers PUROS de la edición de variaciones existentes (la
// tabla de la ficha, PARTE A, y el contrato de escritura, PARTE B). Crear
// variaciones nuevas desde atributos globales queda para después de P2-tax.

import { totalStockVariaciones, skuExistsInCatalog } from './productFormHelpers';
import { fmtMoney } from './orderUtils';
import { opcionCompuesta } from './multiEje';

function slugSku(s) {
  return String(s || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// MARCA-PRODUCTO-VARIANTE: SKU del padre + valor(es) de la variación.
// Tanda 8: `opciones` puede ser un string (mono-eje, legado) o un array de
// {name, option} / de strings (multi-eje) — se combinan en orden: PAD-NEGRO-S.
// El caso mono (string) es byte-idéntico a la 7d (lo fija el golden H9).
export function generateVariationSku(parentSku, opciones) {
  const base = String(parentSku || '').trim();
  if (!base) return '';
  const valores = Array.isArray(opciones)
    ? opciones.map((o) => (o && typeof o === 'object' ? o.option : o))
    : [opciones];
  const sufijos = valores.map(slugSku).filter(Boolean);
  return sufijos.length ? `${base}-${sufijos.join('-')}` : base;
}

// Variación del GET /full → fila editable de la tabla.
export function filaAFormulario(v = {}) {
  const attrsTodos = Array.isArray(v.attributes) ? v.attributes : [];
  const attr = attrsTodos[0] || {};
  const cont = v.contenido && typeof v.contenido === 'object' ? v.contenido : null;
  return {
    id: v.id,
    attributeName: attr.name || '',
    // P2b tonos: id del atributo GLOBAL (id>0) del eje mono — el payload lo enhebra para
    // que Woo ligue la variación a la taxonomía (pa_tono/pa_color). SOLO cuando >0: un
    // atributo local no lleva id (los goldens 7d/H9 quedan byte-idénticos; la regla dura
    // «id:0 nunca viaja» vive además en el backend variacionPayload.js).
    ...(Number(attr.id) > 0 ? { attributeId: Number(attr.id) } : {}),
    option: attr.option || '',
    // Tanda 7d: la fila conserva TODOS los ejes de la variación — attributes[0]
    // hacía indistinguibles las filas de un Color × Talla (tres veces «Negro»).
    opciones: attrsTodos.map((a) => ({ name: (a && a.name) || '', option: (a && a.option) || '', ...(a && Number(a.id) > 0 ? { id: Number(a.id) } : {}) })),
    // Tanda 8: ancla POR EJE de la detección de renombre (espejo multi-eje de
    // optionOriginal). Los valores del GET; comparar contra ellos dice qué eje cambió.
    opcionesOriginal: attrsTodos.map((a) => ({ name: (a && a.name) || '', option: (a && a.option) || '', ...(a && Number(a.id) > 0 ? { id: Number(a.id) } : {}) })),
    opcionCompuesta: opcionCompuesta(attrsTodos),
    optionOriginal: attr.option || '', // el valor del GET: ancla del renombre y de la baja
    sku: v.sku || '',
    // Tanda 4b (defecto 1): el SKU del GET, ancla de la exclusión del aviso de
    // duplicado — un SKU que pertenece a ESTE producto no se acusa a sí mismo.
    skuOriginal: v.sku || '',
    // Tanda 4b (ítem 9): GTIN por variación.
    gtin: v.global_unique_id || '',
    regularPrice: v.regular_price != null ? String(v.regular_price) : '',
    salePrice: v.sale_price != null ? String(v.sale_price) : '',
    stockQty: v.stock_quantity != null ? String(v.stock_quantity) : '',
    contenidoValor: cont && cont.valor != null ? String(cont.valor) : '',
    contenidoUnidad: (cont && cont.unidad) || 'ml',
    image: v.image || null,
    tocados: {},
  };
}

// ¿La fila tiene más de un eje? (multi-eje): la longitud de `opciones` lo dice.
// Una fila mono-eje del GET tiene opciones.length === 1; el fixture golden 7d no
// trae `opciones` (undefined → 0) y también es mono. Ambos caen al camino legado.
function esFilaMultiEje(fila) {
  return ((fila && fila.opciones) || []).length > 1;
}

// Fila → body del endpoint de variación: SOLO lo tocado (regla 3g por fila);
// la presencia de la clave es la marca de tocado. Sin tocados → null.
// Tanda 8: si el eje se tocó, el payload viaja según la dimensionalidad —
// multi-eje manda el SET COMPLETO en `ejes` (el PUT de Woo lo reemplaza, un
// delta borraría el eje que falta; lo valida el backend); mono-eje manda el
// legado `option`/`attributeName`, BYTE-IDÉNTICO a la 7d (lo fija el golden H9).
export function payloadDeFilaTocada(fila) {
  const t = fila.tocados || {};
  const out = { id: fila.id };
  if (t.option) {
    if (esFilaMultiEje(fila)) {
      // P2b tonos: enhebrar el id del eje GLOBAL (id>0); el local no lleva id (regla dura,
      // reforzada en el backend). Woo liga por id a la taxonomía (pa_tono/pa_color).
      out.ejes = (fila.opciones || []).map((o) => (Number(o.id) > 0
        ? { id: Number(o.id), name: o.name, option: o.option }
        : { name: o.name, option: o.option }));
    } else {
      out.option = fila.option; out.attributeName = fila.attributeName;
      if (Number(fila.attributeId) > 0) out.attributeId = Number(fila.attributeId);
    }
  }
  if (t.sku) out.sku = fila.sku;
  if (t.gtin) out.global_unique_id = fila.gtin;
  if (t.regularPrice) out.regular_price = fila.regularPrice;
  if (t.salePrice) out.sale_price = fila.salePrice;
  if (t.stockQty) out.stock_quantity = fila.stockQty;
  if (t.contenidoValor || t.contenidoUnidad) {
    out.contenido = String(fila.contenidoValor || '').trim()
      ? { valor: String(fila.contenidoValor).trim(), unidad: fila.contenidoUnidad || 'ml' }
      : null; // vacío tocado = borrar la meta (hereda del padre)
  }
  return Object.keys(out).length > 1 ? out : null;
}

// Una tocada → PUT individual; varias → batch en UNA llamada; ninguna → nada.
export function planDeEscrituras(filas) {
  const items = (filas || []).map((f) => payloadDeFilaTocada(f)).filter(Boolean);
  if (!items.length) return { modo: 'nada', items: [] };
  return { modo: items.length === 1 ? 'put' : 'batch', items };
}

// ── Tanda 8: renombre de un valor compartido y recálculo del padre ───────────

// Editar la celda (eje `ejeName`) de una fila cuyo valor es `de` renombra `de`→
// `a` en TODAS las variaciones que lo tienen (definición de Miguel: un valor
// compartido se renombra en bloque). Devuelve las filas nuevas (CLONA; el array
// es estado de React) con `opciones` actualizado y `tocados.option` marcado en
// las afectadas. No fusiona: si `a` ya existe en otra combinación, esto NO se
// llama (lo bloquea renombreHaciaExistente).
export function aplicarRenombreEje(filas, ejeName, de, a) {
  return (filas || []).map((f) => {
    const ops = f.opciones || [];
    const idx = ops.findIndex((o) => o.name === ejeName);
    if (idx < 0 || ops[idx].option !== de) return f;
    const opciones = ops.map((o, i) => (i === idx ? { ...o, option: a } : { ...o }));
    return {
      ...f,
      opciones,
      // el compuesto se re-deriva para la tabla; el legado (mono) sigue su option
      opcionCompuesta: opciones.map((o) => o.option).filter(Boolean).join(' · '),
      option: opciones.length > 1 ? f.option : a,
      tocados: { ...f.tocados, option: true },
    };
  });
}

// ¿Cuántas variaciones comparten el valor `de` en el eje `ejeName`? (para el
// aviso previo del renombre — se muestra solo si son 2 o más).
export function afectadasPorRenombre(filas, ejeName, de) {
  return (filas || []).filter((f) => (f.opciones || []).some((o) => o.name === ejeName && o.option === de)).length;
}

// ¿Renombrar el valor `de`→`a` del eje `ejeName` chocaría con un valor que YA
// existe en OTRA combinación de ese eje? Entonces NO es un renombre sino una
// fusión, y crear una combinación se hace SOLO por el diálogo (definición de
// Miguel). true = bloquear.
export function renombreHaciaExistente(filas, ejeName, de, a) {
  const nuevo = String(a || '').trim();
  if (!nuevo || nuevo === de) return false;
  return (filas || []).some((f) => {
    const op = (f.opciones || []).find((o) => o.name === ejeName);
    return op && op.option !== de && op.option === nuevo;
  });
}

// Las options de cada eje del padre = UNIÓN de los valores de sus variaciones
// (definición de Miguel: se recalculan SIEMPRE). Conserva el orden previo del
// padre, agrega los valores nuevos al final y SUELTA los huérfanos (valores que
// ya ninguna variación usa). CLONA (estado de React). Los ejes no-variación
// pasan sin tocar.
export function recalcularOpcionesPadre(attrsPadre, filas) {
  return (attrsPadre || []).map((a) => {
    if (!a.variation) return { ...a };
    const enUso = [];
    for (const f of (filas || [])) {
      const op = (f.opciones || []).find((o) => o.name === a.name);
      if (op && op.option && !enUso.includes(op.option)) enUso.push(op.option);
    }
    const previos = (a.options || []).filter((o) => enUso.includes(o));
    const nuevos = enUso.filter((o) => !previos.includes(o));
    return { ...a, options: [...previos, ...nuevos] };
  });
}

// Los ejes de una fila que cambiaron respecto del GET (multi: opciones vs
// opcionesOriginal; mono: option vs optionOriginal). Base de skuPropuestoAlRenombrar.
function ejesCambiadosDeFila(f) {
  const ops = f.opciones || [];
  if (ops.length > 1) {
    const orig = f.opcionesOriginal || [];
    const cambios = [];
    ops.forEach((o, i) => {
      const de = (orig[i] && orig[i].option) || '';
      if (o.option !== de) cambios.push({ de, a: o.option });
    });
    return cambios;
  }
  if (f.tocados && f.tocados.option && f.option !== f.optionOriginal) return [{ de: f.optionOriginal, a: f.option }];
  return [];
}

// Tanda 7b — ALTA de variaciones sin atributos globales. Devuelve los
// attributes del padre listos para el PUT previo al POST de la variación:
// CLONA (nunca muta — el array original es estado de React y el ancla de la
// REVERSIÓN si el POST falla), crea el atributo propio la primera vez
// (variation:true, visible:true, conservando los atributos no-variación) y
// agrega la opción solo si no estaba. Cuando existan atributos GLOBALES
// (después de P2-tax), el mismo botón ofrecerá elegirlos — registrado en el
// diseño; este helper es el camino "atributo propio del producto".
export function atributosParaAlta(attributes, eje, option) {
  const nombre = String(eje || '').trim();
  const valor = String(option || '').trim();
  const lista = (attributes || []).map((a) => ({ ...a, options: [...(a.options || [])] }));
  if (!nombre || !valor) return lista;
  const attr = lista.find((a) => a.name === nombre && a.variation !== false);
  if (!attr) {
    lista.push({ name: nombre, variation: true, visible: true, options: [valor] });
    return lista;
  }
  if (!attr.options.includes(valor)) attr.options.push(valor);
  return lista;
}

// Reemplaza el valor viejo por el nuevo en las options del atributo del padre
// (CLONA, nunca muta: el array original es el estado de React).
export function renombrarOpcion(attributes, attrName, viejo, nuevo) {
  return (attributes || []).map((a) => (
    a.name === attrName
      ? { ...a, options: (a.options || []).map((o) => (o === viejo ? nuevo : o)) }
      : { ...a }
  ));
}

// ¿La opción queda huérfana si se elimina la fila `idEliminada`?
export function opcionQuedaHuerfana(filas, idEliminada, opcion) {
  return !(filas || []).some((f) => f.id !== idEliminada && f.option === opcion);
}

// Tanda 4b (defecto 1) — el aviso de SKU duplicado de una FILA, sin acusarse a
// sí mismo: tras guardar y refrescar el listado, el catálogo trae de vuelta los
// SKUs de ESTE producto (padre y variaciones) y el chequeo ingenuo disparaba en
// cada fila contra su propio SKU recién generado. Se excluye todo SKU propio
// (el del padre + los ORIGINALES de las filas, que son los que el catálogo
// conoce); dos filas de la misma tabla con el mismo SKU actual sí disparan.
export function skuDuplicadoDeFila(fila, filas, existingSkus, skuPadre) {
  const s = String((fila && fila.sku) || '').trim().toUpperCase();
  if (!s) return false;
  const norm = (x) => String(x || '').trim().toUpperCase();
  if ((filas || []).some((o) => o.id !== fila.id && norm(o.sku) === s)) return true;
  const propios = new Set([skuPadre, ...(filas || []).map((o) => o.skuOriginal)].map(norm).filter(Boolean));
  if (propios.has(s)) return false;
  return skuExistsInCatalog(s, existingSkus);
}

// Tanda 4b (defecto 2) — renombrar un tono NO cambia el SKU solo (puede estar
// impreso en etiquetas/QR). Si el SKU actual contiene el SEGMENTO derivado del
// valor VIEJO, se devuelve el SKU propuesto con el segmento del valor nuevo para
// OFRECERLO; en cualquier otro caso, null (no hay oferta).
// Tanda 8: reemplaza SOLO el segmento del eje que cambió (PAD-NEGRO-S + Negro→
// Azul → PAD-AZUL-S). Si cambió más de un eje, no ofrece (ambiguo). El caso
// mono es el mismo comportamiento de la 7d.
export function skuPropuestoAlRenombrar(fila) {
  const f = fila || {};
  const sku = String(f.sku || '').trim();
  if (!sku) return null;
  const cambios = ejesCambiadosDeFila(f);
  if (cambios.length !== 1) return null;
  const sufViejo = slugSku(cambios[0].de);
  const sufNuevo = slugSku(cambios[0].a);
  if (!sufViejo || !sufNuevo) return null;
  // el segmento va entre guiones o en un extremo — nunca parte de otra palabra
  const re = new RegExp(`(^|-)${sufViejo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(-|$)`);
  if (!re.test(sku)) return null;
  const propuesto = sku.replace(re, (m, pre, post) => `${pre}${sufNuevo}${post}`);
  return propuesto === sku ? null : propuesto;
}

// Tanda 4c (ítem 3): el dato del padre variable se muestra con FORMATO DE DATO
// (moneda y unidades), nunca con formato de ayuda/ejemplo — un dato real que
// parece ejemplo se lee como texto de relleno. Redacción acordada:
// "Precio: de $48.000 a $52.000 según la variación · Stock total: 10 unidades".
export function resumenPadreVariable(filas) {
  const precios = (filas || []).map((f) => parseInt(f.regularPrice, 10)).filter((n) => n > 0);
  const stock = totalStockVariaciones((filas || []).map((f) => ({ stock_quantity: parseInt(f.stockQty, 10) || 0 })));
  const unidades = `${stock} ${stock === 1 ? 'unidad' : 'unidades'}`;
  if (!precios.length) return `Stock total: ${unidades}`;
  const min = Math.min(...precios);
  const max = Math.max(...precios);
  const precio = min === max
    ? `Precio: ${fmtMoney(min)} en todas las variaciones`
    : `Precio: de ${fmtMoney(min)} a ${fmtMoney(max)} según la variación`;
  return `${precio} · Stock total: ${unidades}`;
}
